import React, { useState } from 'react';
import { format } from 'date-fns';
import { Download, Mail, LayoutList } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Textarea } from './ui';
import { wellnessHomeworkService } from '../services/api';

const PILLAR_COLOR_VARIANT = {
  mind: 'purple',
  move: 'emerald',
  journal: 'coral',
  faith: 'amber',
};

const PILLAR_BADGE_CLASSES = {
  mind: 'bg-purple-100 text-purple-800 border-purple-200',
  move: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  journal: 'bg-coral-100 text-coral-800 border-coral-200',
  faith: 'bg-amber-100 text-amber-800 border-amber-200',
};

const STATUS_BADGE_VARIANT = {
  assigned: 'primary',
  'in-progress': 'warning',
  completed: 'success',
  skipped: 'neutral',
};

const MAX_RESPONSE = 5000;

const formatDate = (value) => {
  if (!value) return null;
  try {
    return format(new Date(value), 'MMM d, yyyy');
  } catch (_) {
    return null;
  }
};

const PillarBadge = ({ pillar }) => {
  if (!pillar) return null;
  const classes =
    PILLAR_BADGE_CLASSES[pillar] ||
    'bg-bgSecondary text-textSecondary border-border';
  return (
    <span
      className={`inline-flex items-center font-medium rounded-full px-2.5 py-0.5 text-xs border capitalize ${classes}`}
    >
      {pillar}
    </span>
  );
};

const ActiveHomeworkCard = ({ assignment, onChange }) => {
  const item = assignment?.homeworkItemId || {};
  const area = assignment?.wellnessAreaId || {};
  const source = assignment?.assignmentSource;
  const navigate = useNavigate();

  const interactiveEnabled = Boolean(
    process.env.REACT_APP_INTERACTIVE_HOMEWORK_ENABLED !== 'false'
  );

  const handleOpenInteractive = () => {
    navigate(`/homework/interactive/wellness/${assignment._id}`);
  };

  const [expanded, setExpanded] = useState(
    assignment?.status === 'in-progress'
  );
  const [response, setResponse] = useState(assignment?.userResponse || '');
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [emailingPdf, setEmailingPdf] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [showResponse, setShowResponse] = useState(false);

  const pillar = item.pillar;
  const colorVariant = PILLAR_COLOR_VARIANT[pillar] || null;

  const assignedAtLabel = formatDate(assignment?.assignedAt);
  const receivedAtLabel = formatDate(assignment?.receivedAt);
  const dueDateLabel = formatDate(assignment?.dueDate);
  const completedAtLabel = formatDate(assignment?.completedAt);

  const assignedByLabel = (() => {
    if (source === 'self') return 'Self-assigned';
    if (source === 'clinician' || source === 'coach') {
      return 'Assigned by your care team';
    }
    return null;
  })();

  const notifyParent = () => {
    if (onChange) onChange();
  };

  const getDownloadFileName = (headers, fallback = 'wellness-homework.pdf') => {
    const disposition = headers?.['content-disposition'] || headers?.['Content-Disposition'];
    if (!disposition) return fallback;
    const match = disposition.match(/filename="?([^";]+)"?/i);
    return match?.[1] || fallback;
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      setActionError(null);
      const response = await wellnessHomeworkService.downloadAssignmentPdf(assignment._id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = getDownloadFileName(response.headers, `${item.title || 'wellness-homework'}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      notifyParent();
    } catch (err) {
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to download PDF right now.'
      );
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleEmailPdf = async () => {
    try {
      setEmailingPdf(true);
      setActionError(null);
      setEmailStatus(null);
      const response = await wellnessHomeworkService.emailAssignmentPdf(assignment._id);
      setEmailStatus({
        type: 'success',
        text: response?.data?.message || 'Homework PDF emailed successfully.'
      });
      notifyParent();
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Unable to email PDF right now.';
      setEmailStatus({ type: 'error', text: message });
      setActionError(message);
    } finally {
      setEmailingPdf(false);
    }
  };

  const handleStart = async () => {
    try {
      setSaving(true);
      setActionError(null);
      await wellnessHomeworkService.startAssignment(assignment._id);
      setExpanded(true);
      notifyParent();
    } catch (err) {
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to start this exercise.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    try {
      setSaving(true);
      setActionError(null);
      await wellnessHomeworkService.completeAssignment(
        assignment._id,
        response
      );
      notifyParent();
    } catch (err) {
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to mark as complete.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    try {
      setSaving(true);
      setActionError(null);
      await wellnessHomeworkService.skipAssignment(assignment._id);
      setShowSkipConfirm(false);
      notifyParent();
    } catch (err) {
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to skip this exercise.'
      );
    } finally {
      setSaving(false);
    }
  };

  const statusVariant = STATUS_BADGE_VARIANT[assignment?.status] || 'neutral';
  const statusLabel =
    assignment?.status === 'in-progress'
      ? 'In progress'
      : assignment?.status
        ? assignment.status.charAt(0).toUpperCase() + assignment.status.slice(1)
        : '';

  return (
    <Card
      variant="default"
      colorVariant={colorVariant}
      padding="default"
      borderAccent={Boolean(colorVariant)}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-textPrimary">
              {item.title || 'Wellness exercise'}
            </h3>
            <PillarBadge pillar={pillar} />
            {item.isFaithIntegrated && (
              <span className="inline-flex items-center font-medium rounded-full px-2 py-0.5 text-xs border bg-amber-50 text-amber-800 border-amber-200">
                Faith
              </span>
            )}
          </div>
          {area.displayName && (
            <p className="mt-1 text-sm text-textSecondary">
              {area.displayName}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {statusLabel && (
            <Badge variant={statusVariant} size="sm">
              {statusLabel}
            </Badge>
          )}
          {assignedByLabel && (
            <span className="text-xs text-textMuted">{assignedByLabel}</span>
          )}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-textMuted">
        {assignedAtLabel && <span>Assigned {assignedAtLabel}</span>}
        {receivedAtLabel && <span>Received {receivedAtLabel}</span>}
        {dueDateLabel && <span>Due {dueDateLabel}</span>}
        {assignment?.status === 'completed' && completedAtLabel && (
          <span>Completed {completedAtLabel}</span>
        )}
        {item.estimatedMinutes != null && (
          <span>{item.estimatedMinutes} min</span>
        )}
        {item.difficultyLevel && (
          <span className="capitalize">{item.difficultyLevel}</span>
        )}
      </div>

      {emailStatus && (
        <div
          className={`mt-3 p-2 rounded-md border text-sm ${
            emailStatus.type === 'success'
              ? 'border-success/30 bg-success/10 text-success'
              : 'border-error/30 bg-error/10 text-error'
          }`}
        >
          {emailStatus.text}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {interactiveEnabled && assignment?.status !== 'completed' && assignment?.status !== 'skipped' && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleOpenInteractive}
            disabled={saving}
          >
            <LayoutList className="h-4 w-4" />
            Open Interactive
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          onClick={handleDownloadPdf}
          disabled={downloadingPdf || saving}
        >
          <Download className="h-4 w-4" />
          {downloadingPdf ? 'Downloading...' : 'Download PDF'}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleEmailPdf}
          disabled={emailingPdf || saving}
        >
          <Mail className="h-4 w-4" />
          {emailingPdf ? 'Emailing...' : 'Email PDF'}
        </Button>
      </div>

      {actionError && (
        <div className="mt-3 p-2 rounded-md border border-error/20 bg-error/10 text-error text-sm">
          {actionError}
        </div>
      )}

      {assignment?.status === 'assigned' && (
        <div className="mt-4">
          {item.instructions && !expanded && (
            <p className="text-sm text-textSecondary line-clamp-2">
              {item.instructions}
            </p>
          )}
          {item.instructions && expanded && (
            <div className="text-sm text-textSecondary whitespace-pre-wrap">
              {item.instructions}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? 'Collapse' : 'Expand'}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleStart}
              disabled={saving}
            >
              {saving ? 'Starting...' : 'Start'}
            </Button>
            {!showSkipConfirm ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSkipConfirm(true)}
                disabled={saving}
              >
                Skip
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm text-textSecondary">
                  Skip this exercise?
                </span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleSkip}
                  disabled={saving}
                >
                  {saving ? 'Skipping...' : 'Confirm skip'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSkipConfirm(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {assignment?.status === 'in-progress' && (
        <div className="mt-4 space-y-3">
          {item.instructions && (
            <div className="text-sm text-textSecondary whitespace-pre-wrap">
              {item.instructions}
            </div>
          )}
          <div>
            <Textarea
              label="Your response"
              rows={5}
              maxLength={MAX_RESPONSE}
              value={response}
              onChange={(e) => setResponse(e.target.value.slice(0, MAX_RESPONSE))}
              placeholder="Capture what came up for you..."
              fullWidth
            />
            <div className="mt-1 text-xs text-textMuted text-right">
              {response.length} / {MAX_RESPONSE}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="success"
              size="sm"
              onClick={handleComplete}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Mark Complete'}
            </Button>
            {!showSkipConfirm ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSkipConfirm(true)}
                disabled={saving}
              >
                Skip
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm text-textSecondary">
                  Skip this exercise?
                </span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleSkip}
                  disabled={saving}
                >
                  {saving ? 'Skipping...' : 'Confirm skip'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSkipConfirm(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {assignment?.status === 'completed' && (
        <div className="mt-4">
          {completedAtLabel && (
            <p className="mb-2 text-xs text-textMuted">
              Completion receipt: {completedAtLabel}
            </p>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowResponse((v) => !v)}
          >
            {showResponse ? 'Hide response' : 'View response'}
          </Button>
          {showResponse && (
            <div className="mt-2 p-3 rounded-md bg-bgSecondary border border-border text-sm text-textSecondary whitespace-pre-wrap">
              {assignment?.userResponse ? assignment.userResponse : (
                <span className="text-textMuted italic">
                  No written response was saved.
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {assignment?.status === 'skipped' && (
        <div className="mt-3 text-sm text-textMuted">
          {assignment?.skipReason ? (
            <>Reason: {assignment.skipReason}</>
          ) : (
            <>This exercise was skipped.</>
          )}
        </div>
      )}
    </Card>
  );
};

export default ActiveHomeworkCard;
